let matrixClient = null;
const recoveryPassphrases = new Map();
const LEGACY_RECOVERY_PASSPHRASE_KEY = "key_passphrase";

if (typeof localStorage !== "undefined") {
    localStorage.removeItem(LEGACY_RECOVERY_PASSPHRASE_KEY);
}

const getClientSessionKey = (client) => {
    const userId = client?.getUserId?.();
    const deviceId = client?.getDeviceId?.();
    return userId && deviceId ? `${userId}:${deviceId}` : null;
};
  
export function setMatrixClientSession(client) {
    matrixClient = client;
}

export function getMatrixClientSession() {
    return matrixClient;
}

export function clearMatrixClientSession() {
    matrixClient = null;
}

export const SecurePassphraseStorage = {
    set(passphrase, client) {
        const sessionKey = getClientSessionKey(client);
        if (sessionKey) {
            recoveryPassphrases.set(sessionKey, passphrase);
        }
    },
    get(client) {
        const sessionKey = getClientSessionKey(client);
        return sessionKey ? recoveryPassphrases.get(sessionKey) || null : null;
    },
    clear(client) {
        const sessionKey = getClientSessionKey(client);
        if (sessionKey) {
            recoveryPassphrases.delete(sessionKey);
        }
    },
};

/**
 * Stops the Matrix client while preserving its installation binding and crypto store.
 * The proxy token remains valid across ordinary Me logout/login.
 */
export async function fullMatrixLogout() {
    const client = getMatrixClientSession();

    try {
        if (client) {
            SecurePassphraseStorage.clear(client);
            client.__dailyfixmeChatListenerCleanup?.();
            try {
                client.stopClient?.();
            } catch (err) {
                if (err.name === "AbortError") {
                    console.log("Logout aborted (expected behavior).");
                } else {
                    console.error("Logout error:", err);
                }
            }
            client.removeAllListeners();
            clearMatrixClientSession();
            console.log("[Matrix] Client stopped; device storage preserved.");
        }

    } catch (err) {
        console.error("[Matrix] Client shutdown failed:", err);
    }
}

let cryptoInitPromise = null;
let cryptoInitSessionKey = null;

export const prepareUserCryptoInBackground = (client, passphrase, options) => {
    const crypto = client?.getCrypto();
    const cryptoOptions = options || {};
    if (!crypto) {
        return Promise.resolve(false);
    }

    const sessionKey = `${client.getUserId?.() || ""}:${client.getDeviceId?.() || ""}`;
    if (!cryptoOptions.force && cryptoInitSessionKey === sessionKey && cryptoInitPromise) {
        return cryptoInitPromise;
    }

    cryptoInitSessionKey = sessionKey;
    cryptoInitPromise = runCryptoInit(client, passphrase, cryptoOptions);

    return cryptoInitPromise;
};

export const prepareRoomEncryptionForSend = async (client, room, expectedUserIds) => {
    const crypto = client?.getCrypto?.();
    if (!crypto || !room) {
        throw new Error("Room encryption is not ready. Please try again.");
    }

    try {
        crypto.setTrustCrossSignedDevices?.(true);

        const currentRoom = client.getRoom(room.roomId) || room;
        await currentRoom.loadMembersIfNeeded?.();

        const joinedUserIds = currentRoom.getJoinedMembers()
            .map(member => member.userId)
            .filter(Boolean);
        const userIdsToRefresh = new Set([
            ...joinedUserIds,
            ...(expectedUserIds || []).filter(Boolean),
        ]);

        if (userIdsToRefresh.size > 0) {
            await crypto.getUserDeviceInfo([...userIdsToRefresh], true);
        }
        crypto.prepareToEncrypt?.(currentRoom);
    } catch (err) {
        // The room encryptor performs its own authoritative membership/device
        // refresh during send. Optional warm-up must never block the message.
        console.warn("[Matrix] Recipient device warm-up was incomplete; continuing with Matrix send:", err);
    }
};

/**
 * Tracks every joined member device before this client receives new encrypted events.
 * Matrix normally performs this work on the first send, but receive-only group members
 * must be ready to receive room keys without having to post a message first.
 */
export const prepareRoomEncryptionForReceive = async (client, room) => {
    const crypto = client?.getCrypto?.();
    if (!crypto || !room || room.getMyMembership?.() !== "join") {
        return false;
    }

    try {
        if (!await crypto.isEncryptionEnabledInRoom(room.roomId)) {
            return false;
        }

        const currentRoom = client.getRoom(room.roomId) || room;
        await currentRoom.loadMembersIfNeeded?.();
        const joinedUserIds = currentRoom.getJoinedMembers()
            .map(member => member.userId)
            .filter(Boolean);

        if (joinedUserIds.length > 0) {
            await crypto.getUserDeviceInfo(joinedUserIds, true);
        }
        crypto.prepareToEncrypt?.(currentRoom);
        return true;
    } catch (error) {
        console.warn(`[Matrix] Could not prepare encrypted room ${room.roomId} for receiving yet:`, error);
        return false;
    }
};

export const prepareJoinedRoomsForEncryption = async (client) => {
    const rooms = client?.getRooms?.() || [];
    await Promise.allSettled(
        rooms.map(room => prepareRoomEncryptionForReceive(client, room))
    );
};

async function runCryptoInit(client, passphrase, options) {
    try {
        const crypto = client.getCrypto();

        console.log("[Matrix] Background crypto setup started");

        crypto.setTrustCrossSignedDevices?.(true);

        if (passphrase) {
            try {
                await crypto.bootstrapSecretStorage({
                    createSecretStorageKey: () =>
                        crypto.createRecoveryKeyFromPassphrase(passphrase),
                    setupNewKeyBackup: !!options.setupNewKeyBackup,
                    setupNewSecretStorage: !!options.setupNewSecretStorage,
                });
            } catch (err) {
                if (!options.allowSecretStorageFailure) {
                    throw err;
                }
                console.warn("[Matrix] Secret storage setup was skipped after backup restore:", err);
            }
        }

        let isCrossSigningReady = false;
        try {
            isCrossSigningReady = await crypto.isCrossSigningReady();
        } catch (err) {
            console.warn("[Matrix] Cross-signing check failed:", err);
        }

        if (!isCrossSigningReady) {
            try {
                await crypto.bootstrapCrossSigning({
                    authUploadDeviceSigningKeys: (makeRequest) => makeRequest(),
                });
            } catch (err) {
                if (!options.allowSecretStorageFailure) {
                    throw err;
                }
                console.warn("[Matrix] Cross-signing bootstrap was skipped after backup restore:", err);
            }
        }

        if (passphrase) {
            try {
                await crypto.loadSessionBackupPrivateKeyFromSecretStorage();
            } catch (err) {
                console.warn("[Matrix] Could not load key backup from secret storage yet:", err);
            }
        }

        try {
            await crypto.checkKeyBackupAndEnable();
        } catch (err) {
            if (!options.allowSecretStorageFailure) {
                throw err;
            }
            console.warn("[Matrix] Key backup enable check failed after backup restore:", err);
        }

        try {
            const isSupported = await crypto.isDehydrationSupported();

            if (isSupported) {
                await crypto.startDehydration({
                    rehydrate: true,
                    createNewKey: true,
                    onlyIfKeyCached: false,
                });
            }
        } catch (err) {
            console.warn("[Matrix] Device dehydration setup failed without blocking chat backup:", err);
        }

        console.log("[Matrix] Background crypto setup complete");
        return true;

    } catch (err) {
        console.error("[Matrix] Crypto setup failed:", err);
        cryptoInitPromise = null;
        cryptoInitSessionKey = null;
        return false;
    }
}
